import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, BarChart3, ArrowUpRight, TrendingUp, ShoppingBag, Sparkles, Megaphone, Rocket, BookOpen, Heart, Palette, Briefcase, Languages, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const CATEGORY_ICONS = {
  finanzas: TrendingUp,
  negocios: ShoppingBag,
  tecnologia: Sparkles,
  marketing: Megaphone,
  personal: Rocket,
  salud: Heart,
  creatividad: Palette,
  carrera: Briefcase,
  idiomas: Languages,
  familia: Users,
};

const Courses = ({ courses, onBuy }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("todos");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const filtered =
    selectedCat === "todos"
      ? courses
      : courses.filter((c) => c.category === selectedCat);

  return (
    <section
      id="cursos"
      data-testid="courses-section"
      className="py-24 md:py-32 border-b border-zinc-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-4">
              {t("courses.tag")}
            </p>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter">
              {t("courses.title1")}<br />
              {t("courses.title2")} <span className="text-[#4F46E5]">{t("courses.title3")}</span>
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 md:max-w-sm">
            <p className="text-zinc-600">{t("courses.subtitle")}</p>
            <a
              href="/temario"
              target="_blank"
              rel="noreferrer"
              data-testid="view-syllabus-link"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#4F46E5] hover:text-[#4338CA] underline underline-offset-4"
            >
              {t("courses.viewSyllabus")}
            </a>
          </div>
        </div>

        <div
          data-testid="category-filter"
          className="flex flex-wrap gap-2 mb-12 pb-6 border-b border-zinc-200"
        >
          <button
            onClick={() => setSelectedCat("todos")}
            data-testid="cat-filter-todos"
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-all ${
              selectedCat === "todos"
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
            }`}
          >
            <BookOpen className="w-4 h-4" /> {t("courses.filterAll")}
            <span className="text-xs opacity-70">({courses.length})</span>
          </button>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id] || BookOpen;
            const count = courses.filter((c) => c.category === cat.id).length;
            const active = selectedCat === cat.id;
            const localizedName = t(`courses.categories.${cat.id}`, { defaultValue: cat.name });
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCat(cat.id)}
                data-testid={`cat-filter-${cat.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-all ${
                  active
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                }`}
              >
                <Icon className="w-4 h-4" /> {localizedName}
                <span className="text-xs opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((c, i) => (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
              className="group relative bg-white border border-zinc-200 hover:border-gold transition-all duration-300 flex flex-col hover:glow-gold"
              data-testid={`course-card-${c.id}`}
            >
              {c.popular && (
                <div className="absolute -top-3 left-4 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 z-10">
                  {t("courses.popular")}
                </div>
              )}
              <div className="aspect-[4/3] overflow-hidden bg-zinc-100 relative">
                <img
                  src={c.thumbnail}
                  alt={c.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-mono uppercase">
                  {c.format || "video"}
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-2">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {c.duration}
                  </span>
                  <span className="w-px h-3 bg-zinc-300" />
                  <span className="inline-flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" /> {c.level}
                  </span>
                </div>
                <h3 className="font-display font-bold text-lg leading-snug tracking-tight mb-2">
                  {c.title}
                </h3>
                <p className="text-sm text-zinc-600 mb-4 flex-1 line-clamp-2">{c.subtitle}</p>

                <div className="flex items-end justify-between pt-4 border-t border-zinc-200">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t("courses.perCourse")}
                    </div>
                    <div className="font-display font-black text-2xl">
                      ${c.price}
                      <span className="text-xs text-zinc-500 font-normal ml-1">USD</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/curso/${c.id}`)}
                    data-testid={`course-detail-btn-${c.id}`}
                    className="text-xs font-medium text-zinc-600 hover:text-zinc-900 underline underline-offset-4"
                  >
                    {t("courses.viewDetail")}
                  </button>
                </div>

                <button
                  data-testid={`course-buy-btn-${c.id}`}
                  onClick={() => onBuy(c)}
                  className="mt-4 group/btn w-full inline-flex items-center justify-center gap-1 bg-zinc-900 hover:bg-[#4F46E5] text-white px-4 py-3 text-sm font-semibold transition-colors"
                >
                  {t("courses.buy")}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500">—</div>
        )}
      </div>
    </section>
  );
};

export default Courses;
